import { ReactNode, useMemo } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';

import { Button, Link, Text } from '@spotify-internal/encore-web';
import { useAlert, useAuth } from '@spotify/confidence-core-react';
import { withPageImpression } from '@spotify/plugin-confidence-tracking-react';

import { invitedUserAuthorizationParams } from '../../utils/authLoginParams';
import {
  clearInvitationLoginReturn,
  persistInvitationLoginReturn,
} from '../../utils/invitationLoginReturn';
import { getLoginAuthErrorMessage } from '../SignUp/signupUtils';
import {
  TransitionScreen,
  transitionActionLinkClassNames,
} from '../TransitionScreen';
import { emailLink } from '../utils';

const INVITATION_MISMATCH_ERROR =
  'the specified account is not allowed to accept the current invitation';

type LinkAction = {
  href: string;
  label: string;
};

const supportAction = (message: string): LinkAction => {
  return {
    href: emailLink(
      'Login issue',
      `Page: ${encodeURIComponent(
        global.window.location.href,
      )}%0D%0AError:${message}`,
    ),
    label: 'Contact support',
  };
};

const InvitationPageComponent = () => {
  const alert = useAlert();
  const [searchParams] = useSearchParams();
  const invitation = searchParams.get('invitation');

  const {
    isAuthenticated,
    isLoading: loading,
    loginWithRedirect,
    error,
  } = useAuth();

  const handleLogin = async () => {
    if (searchParams.get('invitation')) {
      persistInvitationLoginReturn(searchParams.toString());
    }

    try {
      await loginWithRedirect({
        appState: { returnTo: '/' },
        ...(invitation
          ? { authorizationParams: invitedUserAuthorizationParams }
          : {}),
      });
    } catch (loginError) {
      alert.post({
        severity: 'error',
        message: getLoginAuthErrorMessage(loginError),
      });
    }
  };

  const { title, message, loginActionLabel, primaryAction, secondaryAction } =
    useMessageProps({
      error,
    });

  if (isAuthenticated && !invitation) {
    clearInvitationLoginReturn();
    return <Navigate to="/" replace />;
  }

  return (
    <TransitionScreen
      title={title}
      description={message}
      actions={
        <>
          {primaryAction ? (
            <Link
              href={primaryAction.href}
              className={transitionActionLinkClassNames.primary}
            >
              {primaryAction.label}
            </Link>
          ) : (
            <Button
              variant="primary"
              onClick={handleLogin}
              size="medium"
              disabled={loading}
            >
              {loginActionLabel ?? 'Sign up'}
            </Button>
          )}
          {secondaryAction && (
            <Link
              href={secondaryAction.href}
              className={transitionActionLinkClassNames.secondary}
            >
              {secondaryAction.label}
            </Link>
          )}
        </>
      }
    />
  );
};

export const InvitationPage = withPageImpression(InvitationPageComponent, {
  pageId: 'invitation',
});

type MessageProps = {
  title: ReactNode;
  message: ReactNode;
  loginActionLabel?: string;
  primaryAction?: LinkAction;
  secondaryAction?: LinkAction;
};

function useMessageProps({ error }: { error: Error | undefined }) {
  const [searchParams] = useSearchParams();
  const hasInvitation = !!searchParams.get('invitation');
  const organization = searchParams.get('organization_name');

  const props = useMemo((): MessageProps => {
    switch (true) {
      case error && hasInvitation && error.message.includes('not found'):
        return {
          title: "Sorry, we couldn't find that invite",
          message: (
            <span>
              If you have previously created an account, you can log into
              Confidence instead. If you are not able to log in, the invite
              might have expired. Ask for a new one and try again.
            </span>
          ),
          primaryAction: {
            href: '/',
            label: 'Go to login page',
          },
          secondaryAction: supportAction(error.message),
        };
      case error &&
        hasInvitation &&
        error.message.includes(INVITATION_MISMATCH_ERROR):
        return {
          title: 'Invitation mismatch',
          message: (
            <span>
              It seems like the email used to login does not match the email on
              the invite. Ask your admin to create another invite.
            </span>
          ),
          loginActionLabel: 'Use a different account',
        };
      case error && error.message.includes('Timeout'):
        return {
          title: 'Login session timed out',
          message: <span>Please try again.</span>,
        };

      case error && error.message.includes('Invalid domain'):
        return {
          title: 'Email domain not allowed',
          message: (
            <span>
              The account does not allow emails from this domain to login.
              Please contact your account admin.
            </span>
          ),
        };
      case hasInvitation:
        return {
          title: 'Welcome to Confidence',
          message: (
            <span>
              You have been invited to{' '}
              {organization ? (
                <span>
                  the{' '}
                  <Text
                    as="span"
                    variant="bodyMedium"
                    semanticColor="textBrightAccent"
                  >
                    {organization}
                  </Text>{' '}
                  workspace
                </span>
              ) : (
                'Confidence'
              )}
              . Sign up to get started!
            </span>
          ),
        };

      default:
        return {
          title: (
            <span>
              Have an idea.
              <br /> Test it. Ship it. <br /> With Confidence.
            </span>
          ),
          message: null,
        };
    }
  }, [error, hasInvitation, organization]);
  return props;
}
